import React from "react";
import {
  TextField,
  MenuItem,
  Typography,
  IconButton,
  Button,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import {
  Close,
  DeleteOutline,
  Add,
  RemoveCircleOutline,
  Translate,
  TouchAppOutlined,
} from "@material-ui/icons";
import {
  QuestionTypes,
  QuestionTypeLabelKeys,
  NodeTypes,
  DiagnosticSectionLabelKeys,
  DEFAULT_LIST_OPTIONS,
  NODE_LABEL_MAX_LENGTH,
  NODE_PROMPT_MAX_LENGTH,
} from "../utils/constants";
import { NodeField } from "../utils/flowSchema";
import DataSourceDropdownComponent from "../../../../Common/Dropdowns/DataSourceDropdownComponent";
import { useFieldStyles } from "./fieldStyles";

const ADDITIONAL_SETTINGS = [
  { key: NodeField.IS_MANDATORY, labelKey: "DiagnosticFlowIsMandatory", id: "diagnostic-flow-node-mandatory" },
  { key: NodeField.HAS_NOT_APPLICABLE, labelKey: "DiagnosticFlowHasNotApplicable", id: "diagnostic-flow-node-not-applicable" },
  { key: NodeField.HAS_COMMENT, labelKey: "DiagnosticFlowHasComment", id: "diagnostic-flow-node-comment" },
  { key: NodeField.HAS_MEDIA_UPFRONT, labelKey: "DiagnosticFlowHasMediaUpfront", id: "diagnostic-flow-node-media-upfront" },
  { key: NodeField.HAS_TIMER, labelKey: "DiagnosticFlowCanHaveTimer", id: "diagnostic-flow-node-timer" },
  { key: NodeField.IS_EXPORTABLE, labelKey: "DiagnosticFlowCanBeExported", id: "diagnostic-flow-node-exportable" },
];

function nodeTypeLabel(node, resources) {
  if (node.type === NodeTypes.START) return resources["Start"];
  if (node.type === NodeTypes.TERMINAL) return resources["End"];
  return resources[QuestionTypeLabelKeys[node.data.questionType]] || resources["Question"];
}

export default function NodeConfigPanel({
  selectedNode,
  onNodeUpdate,
  onNodeDelete,
  onClose,
  onOpenTranslations,
  translationPercent = 0,
  resources = {},
  roles = [],
}) {
  const classes = useFieldStyles();
  if (!selectedNode) {
    return (
      <div
        style={{
          width: "320px",
          borderLeft: "1px solid #e8e8e8",
          background: "#fafafa",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
            textAlign: "center",
            padding: "24px",
            color: "#90A4AE",
            fontSize: "13px",
            lineHeight: "1.6",
            maxWidth: "220px",
          }}
        >
          <div
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              background: "#EDF2F4",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TouchAppOutlined style={{ fontSize: "28px", color: "#90A4AE" }} />
          </div>
          {resources["DiagnosticFlowSelectNodeHint"]}
        </div>
      </div>
    );
  }

  const isQuestion = selectedNode.type === NodeTypes.QUESTION;
  const isTerminal = selectedNode.type === NodeTypes.TERMINAL;
  const isList = selectedNode.data.questionType === QuestionTypes.LIST;
  const listOptions = selectedNode.data[NodeField.LIST_OPTIONS] || DEFAULT_LIST_OPTIONS;

  const handleChange = (field, value) => {
    onNodeUpdate(selectedNode.id, { ...selectedNode.data, [field]: value });
  };

  const updateOptions = (opts) => handleChange(NodeField.LIST_OPTIONS, opts);

  return (
    <div
      style={{
        width: "320px",
        borderLeft: "1px solid #e8e8e8",
        background: "#fff",
        padding: "16px",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
          {resources["DiagnosticFlowEditNode"]}
        </Typography>
        <IconButton id="close-diagnostic-flow-node" size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </div>

      <div>
        <Typography variant="caption" color="textSecondary">
          {resources["DiagnosticFlowNodeType"]}
        </Typography>
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          {nodeTypeLabel(selectedNode, resources)}
        </Typography>
      </div>

      <TextField
        id="diagnostic-flow-node-label"
        label={resources["Label"]}
        required
        value={selectedNode.data[NodeField.LABEL] || ""}
        onChange={(e) => handleChange(NodeField.LABEL, e.target.value)}
        variant="outlined"
        size="small"
        fullWidth
        InputLabelProps={{ className: classes.fieldLabel, title: resources["Label"] }}
        inputProps={{ maxLength: NODE_LABEL_MAX_LENGTH }}
        placeholder={
          isTerminal
            ? resources["DiagnosticFlowTerminalLabelPlaceholder"]
            : resources["DiagnosticFlowLabelPlaceholder"]
        }
      />

      {isQuestion && (
        <>
          <TextField
            id="diagnostic-flow-node-prompt"
            label={resources["Prompt"]}
            required
            value={selectedNode.data[NodeField.PROMPT] || ""}
            onChange={(e) => handleChange(NodeField.PROMPT, e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ className: classes.fieldLabel, title: resources["Prompt"] }}
            inputProps={{ maxLength: NODE_PROMPT_MAX_LENGTH }}
            placeholder={resources["DiagnosticFlowPromptPlaceholder"]}
          />

          <TextField
            id="diagnostic-flow-node-additional-info"
            label={resources["DiagnosticFlowAdditionalInfo"]}
            value={selectedNode.data[NodeField.INFORMATION] || ""}
            onChange={(e) => handleChange(NodeField.INFORMATION, e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={3}
            InputLabelProps={{
              shrink: true,
              className: classes.fieldLabel,
              title: resources["DiagnosticFlowAdditionalInfo"],
            }}
            InputProps={{ className: classes.multilineInput }}
            placeholder={resources["DiagnosticFlowInfoPlaceholder"]}
          />

          <TextField
            id="diagnostic-flow-node-section"
            label={resources["DiagnosticFlowSubsection"]}
            required
            select
            value={selectedNode.data[NodeField.SECTION] || ""}
            onChange={(e) => handleChange(NodeField.SECTION, e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{
              className: classes.fieldLabel,
              title: resources["DiagnosticFlowSubsection"],
            }}
          >
            {Object.entries(DiagnosticSectionLabelKeys).map(([value, key]) => (
              <MenuItem key={value} value={value}>
                {resources[key]}
              </MenuItem>
            ))}
          </TextField>

          <DataSourceDropdownComponent
            id="diagnostic-flow-node-role"
            labelText={resources["DiagnosticFlowRequiredRole"]}
            placeholder={resources["DiagnosticFlowSelectPlaceholder"]}
            value={selectedNode.data[NodeField.REQUIRED_ROLE_ID] || 0}
            values={roles}
            map={(r) => ({ key: r.id, name: r.description })}
            sort={(a, b) => a.description.localeCompare(b.description)}
            handleChange={(e) =>
              handleChange(NodeField.REQUIRED_ROLE_ID, e.target.value || null)
            }
            addMarginBottom={false}
          />

          {isList && (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <Typography variant="caption" style={{ fontWeight: 600 }}>
                {`${resources["DiagnosticFlowListOptions"]} *`}
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ marginTop: "-6px" }}>
                {resources["DiagnosticFlowEachOptionBranch"]}
              </Typography>
              {listOptions.map((opt, i) => (
                <div
                  key={i}
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <TextField
                    id={`diagnostic-flow-node-option-${i}`}
                    value={opt}
                    onChange={(e) => {
                      const opts = [...listOptions];
                      opts[i] = e.target.value;
                      updateOptions(opts);
                    }}
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder={`${resources["DiagnosticFlowOption"]} ${i + 1}`}
                  />
                  <IconButton
                    id={`diagnostic-flow-node-remove-option-${i}`}
                    size="small"
                    disabled={listOptions.length <= 2}
                    onClick={() => {
                      if (listOptions.length <= 2) return;
                      const opts = [...listOptions];
                      opts.splice(i, 1);
                      updateOptions(opts);
                    }}
                  >
                    <RemoveCircleOutline
                      fontSize="small"
                      color={listOptions.length <= 2 ? "disabled" : "error"}
                    />
                  </IconButton>
                </div>
              ))}
              <Button
                id="diagnostic-flow-node-add-option"
                variant="outlined"
                size="small"
                startIcon={<Add />}
                onClick={() => updateOptions([...listOptions, ""])}
                style={{ borderColor: "#00897b", color: "#00897b" }}
              >
                {resources["DiagnosticFlowAddOption"]}
              </Button>
            </div>
          )}

          <div>
            <Typography
              style={{
                fontWeight: 500,
                fontSize: "14px",
                color: "#5C6970",
                display: "block",
                marginBottom: "8px",
              }}
            >
              {resources["DiagnosticFlowAdditionalSettings"]}
            </Typography>
            <div style={{ border: "1px solid #E4EBF0", borderRadius: "4px", overflow: "hidden" }}>
              {ADDITIONAL_SETTINGS.map(({ key, labelKey, id }, index) => {
                const checked = !!selectedNode.data[key];
                return (
                  <div
                    key={key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      minHeight: "48px",
                      padding: "0 6px 0 12px",
                      background: checked ? "#E0F4FF" : "#fff",
                      borderBottom:
                        index < ADDITIONAL_SETTINGS.length - 1 ? "1px solid #E4EBF0" : "none",
                    }}
                  >
                    <Typography variant="body2" style={{ color: "#5C6970" }}>
                      {resources[labelKey]}
                    </Typography>
                    <Checkbox
                      id={id}
                      size="small"
                      checked={checked}
                      onChange={(e) => handleChange(key, e.target.checked)}
                      style={{ color: "#53B3EA" }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      <FormControlLabel
        control={
          <Checkbox
            id="diagnostic-flow-node-is-active"
            size="small"
            color="primary"
            checked={selectedNode.data[NodeField.IS_ACTIVE] !== false}
            onChange={(e) => handleChange(NodeField.IS_ACTIVE, e.target.checked)}
          />
        }
        label={resources["IsActive"]}
      />

      <div
        style={{
          marginTop: "auto",
          paddingTop: "8px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        {onOpenTranslations && selectedNode.type !== NodeTypes.START && (
          <Button
            id="diagnostic-flow-node-translations"
            variant="outlined"
            fullWidth
            startIcon={<Translate />}
            onClick={() => onOpenTranslations(selectedNode.id)}
            style={{
              height: "40px",
              background: "#FFFFFF",
              borderColor: "#DAE1E6",
              color: "#53B3EA",
              fontWeight: 700,
              fontSize: "16px",
              textTransform: "none",
            }}
          >
            {`${resources["Translations"]} (${translationPercent}%)`}
          </Button>
        )}
        {selectedNode.type !== NodeTypes.START && (
          <Button
            id="diagnostic-flow-node-delete"
            variant="outlined"
            fullWidth
            startIcon={<DeleteOutline />}
            onClick={() => onNodeDelete && onNodeDelete(selectedNode.id)}
            style={{
              height: "40px",
              borderColor: "#EA5353",
              color: "#EA5353",
              fontWeight: 700,
              fontSize: "16px",
              textTransform: "none",
            }}
          >
            {resources["DiagnosticFlowDeleteNode"]}
          </Button>
        )}
      </div>
    </div>
  );
}
